import { useEffect, useCallback, useRef } from 'react';
import type { Item, AgentActivity, Status } from '../types';
import { useWebSocket } from './useWebSocket';
import { config } from '../config/env';

export interface QueueMessage {
  type: 'item.added' | 'item.updated' | 'item.status' | 'activity.started' | 'activity.updated' | 'activity.completed' | 'ping';
  data?: Item | AgentActivity | { itemId: string; status: Status } | { itemId: string; activity: AgentActivity };
  timestamp?: number;
}

export interface UseRealtimeQueueOptions {
  /** Enable realtime updates */
  enabled?: boolean;
  /** Callback when item is added */
  onItemAdded?: (item: Item) => void;
  /** Callback when item is updated */
  onItemUpdated?: (item: Item) => void;
  /** Callback when item status changes */
  onItemStatusChange?: (itemId: string, status: Status) => void;
  /** Callback when activity starts */
  onActivityStarted?: (itemId: string, activity: AgentActivity) => void;
  /** Callback when activity is updated (progress update) */
  onActivityUpdated?: (itemId: string, activity: AgentActivity) => void;
  /** Callback when activity completes */
  onActivityCompleted?: (itemId: string, activity: AgentActivity) => void;
}

/**
 * Hook for managing real-time queue updates via WebSocket
 * 
 * @example
 * ```tsx
 * const { status, isConnected } = useRealtimeQueue({
 *   onItemAdded: (item) => setQueue(prev => [item, ...prev]),
 *   onItemUpdated: (item) => setQueue(prev => prev.map(i => i.id === item.id ? item : i)),
 * });
 * ```
 */
export function useRealtimeQueue(options: UseRealtimeQueueOptions = {}) {
  const {
    enabled = true,
    onItemAdded,
    onItemUpdated,
    onItemStatusChange,
    onActivityStarted,
    onActivityUpdated,
    onActivityCompleted,
  } = options;

  // Use refs to store callbacks to avoid recreating handleMessage on every render
  const callbacksRef = useRef({ onItemAdded, onItemUpdated, onItemStatusChange, onActivityStarted, onActivityUpdated, onActivityCompleted });
  
  useEffect(() => {
    callbacksRef.current = { onItemAdded, onItemUpdated, onItemStatusChange, onActivityStarted, onActivityUpdated, onActivityCompleted };
  }, [onItemAdded, onItemUpdated, onItemStatusChange, onActivityStarted, onActivityUpdated, onActivityCompleted]);

  const handleMessage = useCallback((message: QueueMessage | null) => {
    if (!message || !enabled) return;

    const callbacks = callbacksRef.current;

    switch (message.type) {
      case 'item.added':
        if (message.data && 'id' in message.data) {
          callbacks.onItemAdded?.(message.data as Item);
        }
        break;

      case 'item.updated':
        // Handle both full item updates and lightweight updates (just itemId)
        if (message.data) {
          if ('id' in message.data) {
            // Full item update
            callbacks.onItemUpdated?.(message.data as Item);
          } else if ('itemId' in message.data) {
            // Lightweight update - trigger refresh for this item
            callbacks.onItemUpdated?.({ id: (message.data as { itemId: string }).itemId } as Item);
          }
        }
        break;

      case 'item.status':
        if (message.data && 'itemId' in message.data && 'status' in message.data) {
          const { itemId, status } = message.data as { itemId: string; status: Status };
          callbacks.onItemStatusChange?.(itemId, status);
        }
        break;

      case 'activity.started':
        if (message.data && 'itemId' in message.data && 'activity' in message.data) {
          const { itemId, activity } = message.data as { itemId: string; activity: AgentActivity };
          callbacks.onActivityStarted?.(itemId, activity);
        }
        break;

      case 'activity.updated':
        if (message.data && 'itemId' in message.data && 'activity' in message.data) {
          const { itemId, activity } = message.data as { itemId: string; activity: AgentActivity };
          callbacks.onActivityUpdated?.(itemId, activity);
        }
        break;

      case 'activity.completed':
        if (message.data && 'itemId' in message.data && 'activity' in message.data) {
          const { itemId, activity } = message.data as { itemId: string; activity: AgentActivity };
          callbacks.onActivityCompleted?.(itemId, activity);
        }
        break;

      case 'ping':
        // Ignore ping messages to reduce processing
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  }, [enabled]); // Only depend on enabled, not callbacks

  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const { status, lastMessage, sendMessage, disconnect, connect, hasMaxRetries: wsHasMaxRetries } = useWebSocket<QueueMessage>({
    url: enabled ? config.wsUrl : '', // Use config URL when enabled, empty when disabled
    reconnect: enabled,
    reconnectDelay: 3000,
    maxReconnectAttempts,
    onOpen: () => {
      console.log('WebSocket connected for real-time queue updates');
      reconnectAttemptsRef.current = 0;
      // Optionally send a subscription message once connected
      // Use setTimeout to ensure the WebSocket is fully ready
      setTimeout(() => {
        try {
          sendMessage({ type: 'subscribe' as any, channel: 'queue' });
        } catch (err) {
          console.warn('Failed to send subscription message:', err);
        }
      }, 100);
    },
    onClose: () => {
      console.log('WebSocket disconnected');
    },
    onError: () => {
      console.error('WebSocket error');
    },
  });

  // Connect/disconnect based on enabled flag
  // Don't use hasConnectedRef - let useWebSocket handle connection lifecycle
  useEffect(() => {
    if (!enabled) {
      console.log('[useRealtimeQueue] Disabled, disconnecting');
      disconnect();
      return;
    }
    
    // Let useWebSocket handle the connection - it has its own delays and StrictMode handling
    // We just need to ensure the URL is set (which it is via the config)
    console.log('[useRealtimeQueue] Enabled, WebSocket will connect via useWebSocket');
  }, [enabled]); // Only depend on enabled, not status

  // Handle incoming messages with debouncing to prevent overwhelming the UI
  useEffect(() => {
    if (!lastMessage) return;

    // Use a small timeout to batch rapid messages
    const timer = setTimeout(() => {
      handleMessage(lastMessage);
    }, 0); // Process in next tick, but don't delay too much

    return () => clearTimeout(timer);
  }, [lastMessage, handleMessage]);

  const handleReconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  return {
    status,
    isConnected: status === 'connected',
    sendMessage,
    hasMaxRetries: wsHasMaxRetries,
    reconnect: handleReconnect,
  };
}

